import {CSSProperties} from "react";
import PortalContext from "../../../PortalContext";
import Immutable from "../../../../shared/immutable/Immutable";
import {
	Annotation,
	AnnotationRectangle,
	AnnotationsDocument,
	Field,
	Markup,
	MetadataRectangle,
	PagePages,
	Positions
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDocument, {PortalDocumentInterface} from "../../../document/PortalDocument";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {PortalAnnotation} from "../../../../shared/models/PortalAnnotation";
import {PdfPageDefinition} from "../PdfPage";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import CircleUnit from "../../../../shared/units/CircleUnit";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";

export interface AbstractFormFieldProperties extends I18nGlobals, DebugGlobals {
	pageNumber: number;
	zoomValue: number;
	context: PortalContext;
	field: Immutable<Field>;
	portalDocument: Immutable<PortalDocumentInterface>;
}

export default class AbstractFormField<PROPERTY_TYPE extends AbstractFormFieldProperties = any, STATUS_TYPE = any>
	extends PortalPureComponent<PROPERTY_TYPE, STATUS_TYPE> {
	constructor(props: Readonly<PROPERTY_TYPE>) {
		super(props);

		this.getRectangleBoundaries = this.getRectangleBoundaries.bind(this);
		this.computeFieldsStyle = this.computeFieldsStyle.bind(this);
	}

	protected shouldRenderAnnotation(annotation: Annotation): boolean {
		return FunctionHelper.applyValue(annotation.viewable, Annotation.getViewableDefault()) &&
			!FunctionHelper.applyValue(annotation.hidden, Annotation.getHiddenDefault()) &&
			!FunctionHelper.applyValue(annotation.invisible, Annotation.getInvisibleDefault()) &&
			this.props.pageNumber === FunctionHelper.applyValue(annotation.page, Annotation.getPageDefault())
	}

	protected getRectangleBoundaries(annotation: PortalAnnotation): MetadataRectangle {
		let positions: Array<Positions> = FunctionHelper.applyValue(
			annotation.positions, Annotation.getPositionsDefault()
		);

		for (let position of positions) {
			let positionType: AnnotationRectangle = position as AnnotationRectangle;
			if (FunctionHelper.exists(positionType.rectangle)) {
				return positionType.rectangle!;
			}
		}

		return MetadataRectangle.fromJson({height: 0, width: 0, x: 0, y: 0});
	}

	protected getTabIndex(objectKey: string): number {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		let tabIndex: number = FunctionHelper.applyValue(
			portalDocument.metadata?.annotations?.annotation, AnnotationsDocument.getAnnotationDefault()
		).findIndex((annotation: Annotation) => {
			return annotation.objectKey === objectKey;
		});

		if (tabIndex > -1) {
			tabIndex += 1;
		}

		return tabIndex;
	}

	protected computeFieldsStyle(annotation: PortalAnnotation): CSSProperties {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let page: PagePages | undefined = portalDocument.getPage(this.props.pageNumber);
		let scale: number = this.props.zoomValue / 100;
		let pageRotation: number = PdfPageDefinition.calculateRotationSteps(
			FunctionHelper.applyValue(page?.rotation, PagePages.getRotationDefault())
		);
		let rotation: number = 0;
		let boundaries: MetadataRectangle = this.getRectangleBoundaries(annotation);
		let width: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(boundaries.width, 0), LengthUnit.POINTS, this.props.portalResolution
		);
		let height: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(boundaries.height, 0), LengthUnit.POINTS, this.props.portalResolution
		);
		let posX: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(boundaries.x, 0), LengthUnit.POINTS, this.props.portalResolution
		);
		let posY: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(boundaries.y, 0), LengthUnit.POINTS, this.props.portalResolution
		);

		let rotationOffset: number = Math.abs(height - width) / 2;
		let originalX: number = posX + width / 2;
		let originalY: number = posY + height / 2;

		let translateX: number = 0;
		let translateY: number = 0;

		let left: string | undefined;
		let right: string | undefined;
		let top: string | undefined;
		let bottom: string | undefined;

		switch (pageRotation) {
			case 0:
				left = posX + LengthUnit.PIXEL;
				bottom = posY + LengthUnit.PIXEL;
				translateX = originalX * scale - originalX;
				translateY = (originalY * scale - originalY) * -1;
				break;
			case 1:
				top = posX + LengthUnit.PIXEL;
				left = posY + LengthUnit.PIXEL;
				translateX = originalY * scale - originalY;
				translateY = originalX * scale - originalX;

				if (width > height) {
					translateX -= rotationOffset;
					translateY += rotationOffset;
				} else {
					translateX += rotationOffset;
					translateY -= rotationOffset;
				}
				break;
			case 2:
				right = posX + LengthUnit.PIXEL;
				top = posY + LengthUnit.PIXEL;
				translateX = (originalX * scale - originalX) * -1;
				translateY = originalY * scale - originalY;
				break;
			case 3:
				bottom = posX + LengthUnit.PIXEL;
				right = posY + LengthUnit.PIXEL;
				translateX = (originalY * scale - originalY) * -1;
				translateY = (originalX * scale - originalX) * -1;

				if (width > height) {
					translateX += rotationOffset;
					translateY -= rotationOffset;
				} else {
					translateX -= rotationOffset;
					translateY += rotationOffset;
				}
				break;
		}

		return {
			left: left,
			right: right,
			top: top,
			bottom: bottom,
			width: width + LengthUnit.PIXEL,
			height: height + LengthUnit.PIXEL,
			opacity: FunctionHelper.applyValue(annotation.markup?.opacity, Markup.getOpacityDefault()) / 100,
			transform:
				"translate(" + translateX + LengthUnit.PIXEL + ", " + translateY + LengthUnit.PIXEL + ") " +
				"rotate(" + rotation + CircleUnit.DEGREES + ") " +
				"scale(" + scale + ")"
		};
	}
}